import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useListPackages, useCreatePackage, useUpdatePackage, useDeletePackage } from "@workspace/api-client-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Package, Plus, Pencil, Trash2, Check, X, Percent, Clock,
  Users, CalendarClock, RefreshCw, AlertTriangle, Search,
  CalendarX2, TrendingDown, ShieldAlert, Send,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState, useMemo } from "react";

interface PackageForm {
  name: string;
  description: string;
  sessionCount: string;
  priceTotal: string;
  pricePerSession: string;
  savingsPct: string;
  expiryDays: string;
}

const emptyForm: PackageForm = {
  name: "", description: "", sessionCount: "",
  priceTotal: "", pricePerSession: "", savingsPct: "0", expiryDays: "",
};

function getDaysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function ExpiryBadge({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) return <span className="text-xs text-muted-foreground">No expiry</span>;
  const days = getDaysUntil(expiresAt);
  if (days === null) return null;
  const date = new Date(expiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  if (days < 0) return (
    <Badge variant="destructive" className="text-xs gap-1 whitespace-nowrap">
      <CalendarX2 className="w-3 h-3" /> Expired
    </Badge>
  );
  if (days <= 3) return (
    <div className="flex flex-col gap-0.5">
      <Badge className="text-xs gap-1 bg-red-500/20 text-red-400 border-red-500/30 whitespace-nowrap">
        <Clock className="w-3 h-3" /> {days}d left
      </Badge>
      <span className="text-xs text-muted-foreground">{date}</span>
    </div>
  );
  if (days <= 7) return (
    <div className="flex flex-col gap-0.5">
      <Badge className="text-xs gap-1 bg-orange-500/20 text-orange-400 border-orange-500/30 whitespace-nowrap">
        <CalendarClock className="w-3 h-3" /> {days}d left
      </Badge>
      <span className="text-xs text-muted-foreground">{date}</span>
    </div>
  );
  if (days <= 14) return (
    <div className="flex flex-col gap-0.5">
      <Badge className="text-xs gap-1 bg-amber-500/20 text-amber-400 border-amber-500/30 whitespace-nowrap">
        <CalendarClock className="w-3 h-3" /> {days}d left
      </Badge>
      <span className="text-xs text-muted-foreground">{date}</span>
    </div>
  );
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground whitespace-nowrap">{date}</span>
    </div>
  );
}

function riskLevel(purchase: any): "expired" | "urgent" | "warning" | "low" | "empty" | "ok" {
  if (purchase.creditsRemaining === 0) return "empty";
  const days = getDaysUntil(purchase.expiresAt);
  if (days !== null && days < 0) return "expired";
  if (days !== null && days <= 3 && purchase.creditsRemaining > 0) return "urgent";
  if (days !== null && days <= 7 && purchase.creditsRemaining > 0) return "warning";
  if (days !== null && days <= 14 && purchase.creditsRemaining > 0) return "low";
  return "ok";
}

function RiskBadge({ purchase }: { purchase: any }) {
  const risk = riskLevel(purchase);
  if (risk === "expired") return (
    <Badge variant="destructive" className="text-xs gap-1"><CalendarX2 className="w-3 h-3" /> Expired</Badge>
  );
  if (risk === "empty") return (
    <Badge className="text-xs gap-1 bg-white/10 text-muted-foreground border-white/10">All Used</Badge>
  );
  if (risk === "urgent") return (
    <Badge className="text-xs gap-1 bg-red-500/20 text-red-400 border-red-500/30">
      <ShieldAlert className="w-3 h-3" /> At Risk
    </Badge>
  );
  if (risk === "warning") return (
    <Badge className="text-xs gap-1 bg-orange-500/20 text-orange-400 border-orange-500/30">
      <AlertTriangle className="w-3 h-3" /> Expiring Soon
    </Badge>
  );
  if (risk === "low") return (
    <Badge className="text-xs gap-1 bg-amber-500/20 text-amber-400 border-amber-500/30">
      <TrendingDown className="w-3 h-3" /> Watch
    </Badge>
  );
  return null;
}

export default function AdminPackages() {
  const { user } = useAuth();
  const gymId = user?.gymId || "";
  const { data: packages, isLoading, refetch } = useListPackages(gymId, { query: { enabled: !!gymId } });
  const createMutation = useCreatePackage();
  const updateMutation = useUpdatePackage();
  const deleteMutation = useDeletePackage();
  const { toast } = useToast();

  const { data: purchases, isLoading: purchasesLoading } = useQuery({
    queryKey: ["admin", "package-purchases", gymId],
    queryFn: async () => {
      const res = await fetch(`/api/packages/purchases/gym/${gymId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch purchases");
      return res.json() as Promise<any[]>;
    },
    enabled: !!gymId,
  });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PackageForm>(emptyForm);
  const [search, setSearch] = useState("");
  const [filterPackage, setFilterPackage] = useState("all");
  const [filterRisk, setFilterRisk] = useState("all");
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeMessage, setNudgeMessage] = useState("");

  const resetForm = () => { setForm(emptyForm); setShowForm(false); setEditingId(null); };

  const handleEdit = (pkg: any) => {
    setForm({
      name: pkg.name,
      description: pkg.description || "",
      sessionCount: String(pkg.sessionCount),
      priceTotal: String(pkg.priceTotal),
      pricePerSession: String(pkg.pricePerSession),
      savingsPct: String(pkg.savingsPct || 0),
      expiryDays: pkg.expiryDays ? String(pkg.expiryDays) : "",
    });
    setEditingId(pkg.id);
    setShowForm(true);
  };

  const handlePriceChange = (field: "pricePerSession" | "sessionCount", value: string) => {
    const updated = { ...form, [field]: value };
    const perSession = parseFloat(field === "pricePerSession" ? value : form.pricePerSession);
    const count = parseInt(field === "sessionCount" ? value : form.sessionCount);
    if (perSession > 0 && count > 0) updated.priceTotal = (perSession * count).toFixed(2);
    else updated.priceTotal = "";
    setForm(updated);
  };

  const isFormValid = () => {
    if (!form.name.trim()) return false;
    if (!editingId && (!form.sessionCount || parseInt(form.sessionCount) < 1)) return false;
    if (!form.priceTotal || parseFloat(form.priceTotal) <= 0) return false;
    if (!form.pricePerSession || parseFloat(form.pricePerSession) <= 0) return false;
    return true;
  };

  const handleSave = async () => {
    if (!isFormValid()) { toast({ title: "Missing fields", description: "Please fill in all required fields", variant: "destructive" }); return; }
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data: { name: form.name, description: form.description || undefined, priceTotal: parseFloat(form.priceTotal), pricePerSession: parseFloat(form.pricePerSession), savingsPct: parseFloat(form.savingsPct) || 0 } });
        toast({ title: "Package updated" });
      } else {
        await createMutation.mutateAsync({ data: { gymId, name: form.name, description: form.description || undefined, sessionCount: parseInt(form.sessionCount), priceTotal: parseFloat(form.priceTotal), pricePerSession: parseFloat(form.pricePerSession), savingsPct: parseFloat(form.savingsPct) || undefined, expiryDays: form.expiryDays ? parseInt(form.expiryDays) : undefined } });
        toast({ title: "Package created" });
      }
      resetForm();
      refetch();
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleDeactivate = async (id: string) => {
    try { await deleteMutation.mutateAsync({ id }); toast({ title: "Package deactivated" }); refetch(); }
    catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const allPackages = (packages || []) as any[];
  const activePackages = allPackages.filter((p: any) => p.isActive);
  const inactivePackages = allPackages.filter((p: any) => !p.isActive);

  const filteredPurchases = useMemo(() => {
    if (!purchases) return [];
    let list = [...purchases].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => p.studentName?.toLowerCase().includes(q) || p.studentEmail?.toLowerCase().includes(q));
    }
    if (filterPackage !== "all") list = list.filter(p => p.packageId === filterPackage);
    if (filterRisk !== "all") {
      if (filterRisk === "at-risk") list = list.filter(p => ["urgent", "warning", "low"].includes(riskLevel(p)));
      else if (filterRisk === "active") list = list.filter(p => p.creditsRemaining > 0 && riskLevel(p) === "ok");
      else if (filterRisk === "empty") list = list.filter(p => p.creditsRemaining === 0);
    }
    return list;
  }, [purchases, search, filterPackage, filterRisk]);

  const atRiskCount = useMemo(() =>
    (purchases || []).filter(p => ["urgent", "warning"].includes(riskLevel(p))).length
  , [purchases]);

  const filteredStudentIds = useMemo(() => {
    const ids = filteredPurchases.map((p: any) => p.studentId as string);
    return [...new Set(ids)];
  }, [filteredPurchases]);

  const nudgeMutation = useMutation({
    mutationFn: async ({ studentIds, message }: { studentIds: string[]; message: string }) => {
      const res = await fetch("/api/admin/packages/bulk-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ studentIds, message }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || "Failed to send notifications");
      }
      return res.json() as Promise<{ sent: number; failed: number }>;
    },
    onSuccess: (data) => {
      toast({
        title: `Nudge sent to ${data.sent} student${data.sent === 1 ? "" : "s"}`,
        description: data.failed > 0 ? `${data.failed} failed to send.` : "All notifications delivered successfully.",
      });
      setNudgeOpen(false);
      setNudgeMessage("");
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleOpenNudge = () => {
    const atRiskIds = filteredPurchases
      .filter((p: any) => ["urgent", "warning", "low"].includes(riskLevel(p)))
      .map((p: any) => p.studentId as string);
    const defaultMessage = atRiskIds.length > 0 && filterRisk === "at-risk"
      ? "Hi! Just a reminder that your session package is expiring soon and you still have unused credits. Don't let them go to waste — book a session today!"
      : "Hi! Just a reminder that you have unused session credits available. We'd love to see you on the mat — book a session today!";
    setNudgeMessage(defaultMessage);
    setNudgeOpen(true);
  };

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Session Packages</h1>
            <p className="text-muted-foreground text-lg">Manage packages and track student purchases</p>
          </div>
          {!showForm && (
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-primary text-black font-bold">
              <Plus className="w-4 h-4 mr-2" /> New Package
            </Button>
          )}
        </div>

        <Tabs defaultValue="packages" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 h-10 mb-2">
            <TabsTrigger value="packages" className="data-[state=active]:bg-primary data-[state=active]:text-black font-semibold px-5">
              Packages
              {allPackages.length > 0 && (
                <span className="ml-2 bg-white/10 text-xs rounded-full px-2 py-0.5">{activePackages.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="purchases" className="data-[state=active]:bg-primary data-[state=active]:text-black font-semibold px-5">
              Purchase History
              {atRiskCount > 0 && (
                <Badge className="ml-2 bg-red-500/20 text-red-400 border-red-500/30 text-xs px-1.5 py-0">{atRiskCount} at risk</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── PACKAGES TAB ── */}
          <TabsContent value="packages" className="mt-4 space-y-8">
            {showForm && (
              <Card className="glass-panel border-primary/30">
                <CardHeader>
                  <CardTitle className="text-xl text-white">{editingId ? "Edit Package" : "Create New Package"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Package Name</Label>
                      <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., 10-Session Pack" className="bg-white/5 border-white/10 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Number of Sessions</Label>
                      <Input type="number" min="1" value={form.sessionCount} onChange={e => handlePriceChange("sessionCount", e.target.value)} placeholder="10" className="bg-white/5 border-white/10 text-white" disabled={!!editingId} />
                      {editingId && <p className="text-xs text-muted-foreground">Session count cannot be changed after creation</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Price Per Session ($)</Label>
                      <Input type="number" step="0.01" value={form.pricePerSession} onChange={e => handlePriceChange("pricePerSession", e.target.value)} placeholder="20.00" className="bg-white/5 border-white/10 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Total Price ($)</Label>
                      <Input type="number" step="0.01" value={form.priceTotal} readOnly className="bg-white/5 border-white/10 text-white opacity-70 cursor-not-allowed" placeholder="200.00" />
                      <p className="text-xs text-muted-foreground">Auto-calculated</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Savings (%)</Label>
                      <Input type="number" min="0" max="100" step="1" value={form.savingsPct} onChange={e => setForm({ ...form, savingsPct: e.target.value })} placeholder="15" className="bg-white/5 border-white/10 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Expiry (Days)</Label>
                      <Input type="number" min="1" value={form.expiryDays} onChange={e => setForm({ ...form, expiryDays: e.target.value })} placeholder="90" className="bg-white/5 border-white/10 text-white" disabled={!!editingId} />
                      <p className="text-xs text-muted-foreground">{editingId ? "Cannot change after creation" : "Leave blank for no expiry"}</p>
                    </div>
                  </div>
                  {parseFloat(form.priceTotal) > 0 && parseFloat(form.savingsPct) > 0 && (
                    <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3 flex items-center gap-3">
                      <Percent className="w-5 h-5 text-emerald-400 shrink-0" />
                      <div>
                        <p className="text-sm text-emerald-400 font-medium">Students save {form.savingsPct}% vs. individual sessions</p>
                        <p className="text-xs text-muted-foreground">${parseFloat(form.pricePerSession || "0").toFixed(2)} per session</p>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-white">Description</Label>
                    <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description" className="bg-white/5 border-white/10 text-white" />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending || !isFormValid()} className="bg-primary text-black font-bold">
                      <Check className="w-4 h-4 mr-2" /> {editingId ? "Update" : "Create"} Package
                    </Button>
                    <Button variant="ghost" onClick={resetForm} className="text-muted-foreground">
                      <X className="w-4 h-4 mr-2" /> Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[1, 2, 3].map(i => <div key={i} className="h-48 bg-white/5 rounded-2xl animate-pulse" />)}</div>
            ) : (
              <>
                {activePackages.length > 0 && (
                  <div>
                    <h2 className="text-lg font-bold text-white mb-4 uppercase tracking-wider">Active Packages</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {activePackages.map((pkg: any) => (
                        <Card key={pkg.id} className="glass-panel border-white/5 hover:-translate-y-1 transition-all">
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                                <Package className="w-6 h-6 text-primary" />
                              </div>
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-white" onClick={() => handleEdit(pkg)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-400" onClick={() => handleDeactivate(pkg.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <h3 className="text-xl font-bold text-white mb-1">{pkg.name}</h3>
                            {pkg.description && <p className="text-sm text-muted-foreground mb-3">{pkg.description}</p>}
                            <div className="text-3xl font-mono font-bold text-white mb-4">
                              ${pkg.priceTotal}<span className="text-sm text-muted-foreground font-normal"> total</span>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-primary" /><span className="text-white">{pkg.sessionCount} sessions</span></div>
                              <div className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-primary" /><span className="text-white">${pkg.pricePerSession.toFixed(2)} per session</span></div>
                              {pkg.savingsPct > 0 && <div className="flex items-center gap-2 text-sm"><Percent className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400 font-medium">{pkg.savingsPct}% savings</span></div>}
                              {pkg.expiryDays && <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-muted-foreground" /><span className="text-muted-foreground">Expires in {pkg.expiryDays} days</span></div>}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                {inactivePackages.length > 0 && (
                  <div>
                    <h2 className="text-lg font-bold text-muted-foreground mb-4 uppercase tracking-wider">Inactive Packages</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {inactivePackages.map((pkg: any) => (
                        <Card key={pkg.id} className="glass-panel border-white/5 opacity-50">
                          <CardContent className="p-6">
                            <h3 className="text-xl font-bold text-white mb-1">{pkg.name}</h3>
                            <div className="text-2xl font-mono font-bold text-white mb-2">${pkg.priceTotal}<span className="text-sm text-muted-foreground font-normal"> total</span></div>
                            <p className="text-sm text-muted-foreground">{pkg.sessionCount} sessions • ${pkg.pricePerSession.toFixed(2)}/session • {pkg.savingsPct || 0}% savings</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                {activePackages.length === 0 && inactivePackages.length === 0 && (
                  <div className="text-center py-16">
                    <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
                    <h3 className="text-xl font-bold text-white mb-2">No session packages yet</h3>
                    <p className="text-muted-foreground mb-6">Create your first package to offer bundled sessions to students</p>
                    <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-primary text-black font-bold">
                      <Plus className="w-4 h-4 mr-2" /> Create First Package
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ── PURCHASE HISTORY TAB ── */}
          <TabsContent value="purchases" className="mt-4 space-y-4">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by student name or email…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 bg-white/5 border-white/10 text-white"
                />
              </div>
              <select
                value={filterPackage}
                onChange={e => setFilterPackage(e.target.value)}
                className="bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 h-10 min-w-[180px]"
              >
                <option value="all">All Packages</option>
                {allPackages.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <select
                value={filterRisk}
                onChange={e => setFilterRisk(e.target.value)}
                className="bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 h-10 min-w-[140px]"
              >
                <option value="all">All Statuses</option>
                <option value="at-risk">At Risk</option>
                <option value="active">Active (OK)</option>
                <option value="empty">All Used</option>
              </select>
              <Button
                onClick={handleOpenNudge}
                disabled={filteredStudentIds.length === 0}
                className="bg-primary text-black font-bold gap-2 shrink-0"
              >
                <Send className="w-4 h-4" />
                Notify {filteredStudentIds.length > 0 ? filteredStudentIds.length : ""} Student{filteredStudentIds.length !== 1 ? "s" : ""}
              </Button>
            </div>

            {/* Summary stats */}
            {purchases && purchases.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Total Purchases", value: purchases.length, icon: Users, color: "text-primary" },
                  { label: "Active Credits", value: (purchases as any[]).reduce((s, p) => s + (p.creditsRemaining || 0), 0), icon: Package, color: "text-primary" },
                  { label: "Expiring ≤7 Days", value: (purchases as any[]).filter(p => { const d = getDaysUntil(p.expiresAt); return d !== null && d >= 0 && d <= 7 && p.creditsRemaining > 0; }).length, icon: AlertTriangle, color: "text-orange-400" },
                  { label: "At Risk (≤3 Days)", value: atRiskCount, icon: ShieldAlert, color: "text-red-400" },
                ].map(stat => (
                  <Card key={stat.label} className="glass-panel border-white/5 p-4">
                    <div className="flex items-center gap-3">
                      <stat.icon className={`w-5 h-5 ${stat.color} shrink-0`} />
                      <div>
                        <p className={`text-2xl font-mono font-bold ${stat.color}`}>{stat.value}</p>
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Table */}
            {purchasesLoading ? (
              <div className="space-y-2">{[1, 2, 3, 4, 5].map(i => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />)}</div>
            ) : filteredPurchases.length === 0 ? (
              <Card className="glass-panel border-white/5 p-10 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
                <p className="text-white font-semibold text-lg">
                  {purchases && purchases.length > 0 ? "No results match your filters" : "No purchases yet"}
                </p>
                <p className="text-muted-foreground text-sm mt-2">
                  {purchases && purchases.length > 0 ? "Try adjusting your search or filters." : "Student purchases will appear here once packages are bought."}
                </p>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredPurchases.map((p: any) => {
                  const risk = riskLevel(p);
                  const pct = p.creditsTotal > 0 ? (p.creditsRemaining / p.creditsTotal) * 100 : 0;
                  const barColor = risk === "urgent" ? "bg-red-500" : risk === "warning" ? "bg-orange-500" : risk === "low" ? "bg-amber-500" : risk === "empty" ? "bg-white/10" : "bg-primary";
                  const rowBorder = risk === "urgent" ? "border-red-500/30" : risk === "warning" ? "border-orange-500/20" : risk === "low" ? "border-amber-500/20" : "border-white/5";

                  return (
                    <Card key={p.id} className={`glass-panel ${rowBorder} p-4`}>
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        {/* Student */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <p className="font-semibold text-white text-sm truncate">{p.studentName}</p>
                            <RiskBadge purchase={p} />
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{p.studentEmail}</p>
                        </div>

                        {/* Package */}
                        <div className="hidden md:block w-36 shrink-0">
                          <p className="text-sm text-white font-medium truncate">{p.packageName}</p>
                          <p className="text-xs text-muted-foreground">
                            {p.paidAt ? new Date(p.paidAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                          </p>
                        </div>

                        {/* Credits */}
                        <div className="w-full md:w-48 shrink-0">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-muted-foreground">{p.creditsUsed}/{p.creditsTotal} used</span>
                            <span className={`font-mono font-bold ${risk === "urgent" ? "text-red-400" : risk === "warning" ? "text-orange-400" : risk === "low" ? "text-amber-400" : "text-primary"}`}>
                              {p.creditsRemaining} left
                            </span>
                          </div>
                          <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>

                        {/* Expiry */}
                        <div className="hidden md:block w-28 shrink-0">
                          <ExpiryBadge expiresAt={p.expiresAt} />
                        </div>

                        {/* Auto-renew */}
                        <div className="hidden md:flex w-20 shrink-0 justify-end">
                          {p.autoRenew ? (
                            <Badge variant="outline" className="border-primary/40 text-primary text-xs gap-1">
                              <RefreshCw className="w-3 h-3" /> Auto
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">Manual</span>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ── BULK NUDGE DIALOG ── */}
      <Dialog open={nudgeOpen} onOpenChange={open => { if (!open) setNudgeOpen(false); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Notify {filteredStudentIds.length} Student{filteredStudentIds.length !== 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              This will send an in-app notification (and email/SMS if enabled) to{" "}
              <span className="font-semibold text-white">{filteredStudentIds.length} unique student{filteredStudentIds.length !== 1 ? "s" : ""}</span>{" "}
              matching the current filters. Each student receives exactly one message regardless of how many packages they have.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Label className="text-white text-sm font-medium">Message</Label>
            <Textarea
              value={nudgeMessage}
              onChange={e => setNudgeMessage(e.target.value)}
              rows={5}
              placeholder="Write a custom message for these students…"
              className="bg-white/5 border-white/10 text-white resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {nudgeMessage.trim().length} characters · Tip: keep it short and action-oriented.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setNudgeOpen(false)}
              disabled={nudgeMutation.isPending}
              className="text-muted-foreground"
            >
              Cancel
            </Button>
            <Button
              onClick={() => nudgeMutation.mutate({ studentIds: filteredStudentIds, message: nudgeMessage })}
              disabled={nudgeMutation.isPending || !nudgeMessage.trim() || filteredStudentIds.length === 0}
              className="bg-primary text-black font-bold gap-2"
            >
              <Send className="w-4 h-4" />
              {nudgeMutation.isPending ? "Sending…" : `Send to ${filteredStudentIds.length} Student${filteredStudentIds.length !== 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
